import { NavLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { useTemplateConfig } from '../configContext';

interface MobileBottomTabsProps {
  onOpenMenu: () => void;
  /** Optional handler when a `search` tab is tapped. Defaults to focusing the
   *  header search input on the page. */
  onFocusSearch?: () => void;
}

/** Mobile-only floating bottom tab bar (adapted from reactbits-pro
 *  navigation-8: floating pill with blurred background). Hidden at `lg`
 *  and above. */
export default function MobileBottomTabs({ onOpenMenu, onFocusSearch }: MobileBottomTabsProps) {
  const config = useTemplateConfig();
  const reduceMotion = useReducedMotion() ?? false;
  const tabs = config.bottomTabs;
  return (
    <motion.nav
      aria-label="Bottom navigation"
      initial={reduceMotion ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.5, ease: [0.4, 0, 0.2, 1] }}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 lg:hidden"
      style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
    >
      <div className="pointer-events-auto flex items-center justify-around gap-1 rounded-3xl border border-[var(--ce-color-border)] bg-white/80 px-2 py-2 shadow-xl backdrop-blur-md supports-[not(backdrop-filter:blur(1px))]:bg-white">
        {tabs.map((t) => {
          const baseClasses =
            'flex flex-col items-center justify-center gap-0.5 rounded-2xl px-3 py-1.5 text-[10.5px] min-w-[60px] transition-colors';
          const subtleClasses =
            'text-[var(--ce-color-text-subtle)] hover:text-[var(--ce-color-ink)]';

          if (t.action === 'menu') {
            return (
              <button
                key={t.id}
                type="button"
                onClick={onOpenMenu}
                className={`${baseClasses} ${subtleClasses}`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          }
          if (t.action === 'search') {
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  if (onFocusSearch) {
                    onFocusSearch();
                    return;
                  }
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  const input = document.querySelector<HTMLInputElement>(
                    'header input[type="search"][aria-label="Global search"]'
                  );
                  input?.focus();
                }}
                className={`${baseClasses} ${subtleClasses}`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          }
          return (
            <NavLink
              key={t.id}
              to={t.to ?? '#'}
              className={({ isActive }) =>
                `${baseClasses} ${
                  isActive
                    ? 'bg-[var(--ce-color-border)]/40 font-semibold text-[var(--ce-color-ink)]'
                    : subtleClasses
                }`
              }
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </NavLink>
          );
        })}
      </div>
    </motion.nav>
  );
}
